import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { UserContext } from '../../Context/UserContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Chart from 'chart.js/auto';
import './PosBalance.css';

function PosBalance() {
    const navigate = useNavigate();
    const [balanceData, setBalanceData] = useState({
        dineIn: { totalOrders: 0, totalRevenue: 0 },
        onlineDelivery: { totalOrders: 0, totalRevenue: 0 },
        takeAway: { totalOrders: 0, totalRevenue: 0 },
    });

    const [paymentData, setPaymentData] = useState({
        cash: { totalOrders: 0, totalRevenue: 0 },
        card: { totalOrders: 0, totalRevenue: 0 },
        upi: { totalOrders: 0, totalRevenue: 0 },
    });

    const [totalRevenue, setTotalRevenue] = useState(0);
    const [hourlyBreakdown, setHourlyBreakdown] = useState([]);
    const [dailyBreakdown, setDailyBreakdown] = useState([]);
    const [topItemsData, setTopItemsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [filterOrderType, setFilterOrderType] = useState('');
    const [filterPaymentMode, setFilterPaymentMode] = useState('');
    const [showList, setShowList] = useState(false);

    const chartRefs = useRef({});

    // Permissions
    const [canRead, setCanRead] = useState(false);
    const [canWrite, setCanWrite] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [canCreate, setCanCreate] = useState(false);

    // Context for global config
    const { baseUrl, configLoading } = useContext(UserContext);

    useEffect(() => {
        if (configLoading) return;

        const initializeData = async () => {
            await fetchPermissions();
            if (canRead) {
                fetchSalesData();
            }
        };

        initializeData();
    }, [baseUrl, configLoading, fromDate, toDate, filterOrderType, filterPaymentMode, canRead]);

    useEffect(() => {
        if (hourlyBreakdown.length > 0 || dailyBreakdown.length > 0 || topItemsData.length > 0) {
            renderCharts();
        }
        return () => {
            // Cleanup charts on unmount or re-render
            Object.values(chartRefs.current).forEach(chart => chart.destroy());
            chartRefs.current = {};
        };
    }, [hourlyBreakdown, dailyBreakdown, topItemsData, balanceData, paymentData]);

    const fetchPermissions = async () => {
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) return;
            const userObj = JSON.parse(userStr);
            const role = userObj.role;
            if (!role) return;

            const url = `${baseUrl}/api/role-permissions?role=${role}`;
            const response = await axios.get(url);
            const perms = response.data.permissions || [];
            const pagePerm = perms.find(p => p.pageId === 'pos_balance');
            if (pagePerm) {
                setCanRead(pagePerm.canRead === true);
                setCanWrite(pagePerm.canWrite === true);
                setCanDelete(pagePerm.canDelete === true);
                setCanCreate(pagePerm.canCreate === true);
            }
        } catch (error) {
            console.error("Error fetching permissions:", error);
        }
    };

    const fetchSalesData = async () => {
        setLoading(true);
        let url = `${baseUrl}/api/sales`;
        const params = new URLSearchParams();
        if (fromDate) params.append('fromDate', fromDate.toISOString().split('T')[0]);
        if (toDate) params.append('toDate', toDate.toISOString().split('T')[0]);
        if (filterOrderType) params.append('orderType', filterOrderType);
        if (filterPaymentMode) params.append('paymentMode', filterPaymentMode);
        if (params.toString()) url += `?${params.toString()}`;

        try {
            const response = await axios.get(url);
            const cleanedData = cleanData(response.data);
            processSalesData(cleanedData);
            setLoading(false);
        } catch (err) {
            setError('Error fetching sales data: ' + err.message);
            setLoading(false);
        }
    };

    const normalizeOrderType = (orderType) => {
        if (!orderType) return 'N/A';
        const normalized = orderType.trim().toLowerCase();
        const orderTypeMap = {
            'dine in': 'Dine In',
            'dine-in': 'Dine In',
            takeaway: 'Takeaway',
            'take away': 'Takeaway',
            'take-away': 'Takeaway',
            'online delivery': 'Online Delivery',
            delivery: 'Online Delivery',
        };
        return orderTypeMap[normalized] || 'N/A';
    };

    const normalizePaymentMode = (paymentMode) => {
        if (!paymentMode) return 'N/A';
        const normalized = paymentMode.trim().toLowerCase();
        const paymentModeMap = {
            cash: 'Cash',
            card: 'Card',
            upi: 'UPI',
        };
        return paymentModeMap[normalized] || 'N/A';
    };

    const cleanData = (data) => {
        if (!Array.isArray(data)) return [];
        return data
            .filter((sale) => {
                const isValid =
                    sale.items &&
                    sale.items.length > 0 &&
                    !isNaN(sale.grand_total) &&
                    sale.grand_total !== null &&
                    !isNaN(sale.total) &&
                    sale.total !== null &&
                    sale.invoice_no &&
                    sale.date;
                return isValid;
            })
            .map((sale) => ({
                ...sale,
                orderType: normalizeOrderType(sale.orderType),
                paymentMode: normalizePaymentMode(sale.payments?.[0]?.mode_of_payment),
                date: sale.date,
            }));
    };

    const parseDate = (dateStr) => {
        if (!dateStr) return null;
        const parts = dateStr.split('-');
        return parts.length === 3 ? new Date(parts[0], parts[1] - 1, parts[2]) : null;
    };

    const isDateInRange = (saleDate, from, to) => {
        const saleDateObj = parseDate(saleDate);
        if (!saleDateObj) return false;
        const fromDateObj = from ? new Date(from) : null;
        const toDateObj = to ? new Date(to) : null;

        if (!fromDateObj && !toDateObj) return true;
        if (fromDateObj && !toDateObj) return saleDateObj >= fromDateObj;
        if (!fromDateObj && toDateObj) return saleDateObj <= toDateObj;
        return saleDateObj >= fromDateObj && saleDateObj <= toDateObj;
    };

    const processSalesData = (salesData) => {
        const dineIn = { totalOrders: 0, totalRevenue: 0 };
        const onlineDelivery = { totalOrders: 0, totalRevenue: 0 };
        const takeAway = { totalOrders: 0, totalRevenue: 0 };

        const cash = { totalOrders: 0, totalRevenue: 0 };
        const card = { totalOrders: 0, totalRevenue: 0 };
        const upi = { totalOrders: 0, totalRevenue: 0 };

        const hourlyData = {};
        const dailyData = {};
        const itemSales = {};
        let totalRev = 0;

        const filteredData = salesData.filter((sale) => {
            const dateMatch = isDateInRange(sale.date, fromDate, toDate);
            const orderTypeMatch = filterOrderType ? sale.orderType === filterOrderType : true;
            const paymentModeMatch = filterPaymentMode ? sale.paymentMode === filterPaymentMode : true;
            return dateMatch && orderTypeMatch && paymentModeMatch;
        });

        filteredData.forEach((sale) => {
            const grandTotal = parseFloat(sale.grand_total) || 0;
            const orderType = sale.orderType;
            const paymentMode = sale.paymentMode;
            const dateStr = sale.date;

            // Order Types
            if (orderType === 'Dine In') {
                dineIn.totalOrders += 1; dineIn.totalRevenue += grandTotal;
            } else if (orderType === 'Online Delivery') {
                onlineDelivery.totalOrders += 1; onlineDelivery.totalRevenue += grandTotal;
            } else if (orderType === 'Takeaway') {
                takeAway.totalOrders += 1; takeAway.totalRevenue += grandTotal;
            }

            // Payment Modes
            if (paymentMode === 'Cash') {
                cash.totalOrders += 1; cash.totalRevenue += grandTotal;
            } else if (paymentMode === 'Card') {
                card.totalOrders += 1; card.totalRevenue += grandTotal;
            } else if (paymentMode === 'UPI') {
                upi.totalOrders += 1; upi.totalRevenue += grandTotal;
            }

            totalRev += grandTotal;

            // Hourly Data
            const time = sale.time || '00:00:00';
            const hour = parseInt(time.split(':')[0], 10);
            const hourKey = `${hour.toString().padStart(2, '0')}-${(hour + 1).toString().padStart(2, '0')}`;
            if (!hourlyData[hourKey]) {
                hourlyData[hourKey] = { totalOrders: 0, totalRevenue: 0, paymentMode: sale.paymentMode };
            }
            hourlyData[hourKey].totalOrders += 1;
            hourlyData[hourKey].totalRevenue += grandTotal;

            // Daily Data
            if (!dailyData[dateStr]) {
                dailyData[dateStr] = { totalRevenue: 0, totalOrders: 0 };
            }
            dailyData[dateStr].totalRevenue += grandTotal;
            dailyData[dateStr].totalOrders += 1;

            // Top Items Data
            if (sale.items && Array.isArray(sale.items)) {
                sale.items.forEach(item => {
                    const name = item.name || item.item_name || 'Unknown Item';
                    const qty = parseInt(item.quantity || item.qty || 1, 10);
                    if (!itemSales[name]) itemSales[name] = 0;
                    itemSales[name] += qty;
                });
            }
        });

        const fullHourlyBreakdown = [];
        for (let i = 0; i < 24; i++) {
            const hourKey = `${i.toString().padStart(2, '0')}-${(i + 1).toString().padStart(2, '0')}`;
            fullHourlyBreakdown.push({
                timeSlot: hourKey,
                totalOrders: hourlyData[hourKey]?.totalOrders || 0,
                totalRevenue: hourlyData[hourKey]?.totalRevenue || 0,
                paymentMode: hourlyData[hourKey]?.paymentMode || 'All',
            });
        }

        const fullDailyBreakdown = Object.keys(dailyData)
            .sort()
            .map(date => {
                const dateObj = new Date(date);
                const formattedDate = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                return {
                    date: formattedDate,
                    totalRevenue: dailyData[date].totalRevenue,
                    totalOrders: dailyData[date].totalOrders
                };
            });

        const top5Items = Object.keys(itemSales)
            .map(name => ({ name, qty: itemSales[name] }))
            .sort((a, b) => b.qty - a.qty)
            .slice(0, 5);

        setBalanceData({ dineIn, onlineDelivery, takeAway });
        setPaymentData({ cash, card, upi });
        setTotalRevenue(totalRev);
        setHourlyBreakdown(fullHourlyBreakdown);
        setDailyBreakdown(fullDailyBreakdown);
        setTopItemsData(top5Items);
    };

    const renderCharts = () => {
        // Destroy existing charts to prevent duplication
        Object.values(chartRefs.current).forEach(chart => {
            if (chart) chart.destroy();
        });
        chartRefs.current = {};

        Chart.defaults.font.family = "'Inter', sans-serif";
        Chart.defaults.color = '#64748b';

        // 1. Hourly Chart (Mixed Bar + Line)
        const hourlyCtx = document.getElementById('hourlyChart');
        if (hourlyCtx) {
            const gradient = hourlyCtx.getContext('2d').createLinearGradient(0, 0, 0, 400);
            gradient.addColorStop(0, 'rgba(85, 85, 194, 0.4)');
            gradient.addColorStop(1, 'rgba(85, 85, 194, 0.0)');

            chartRefs.current.hourly = new Chart(hourlyCtx, {
                type: 'bar',
                data: {
                    labels: hourlyBreakdown.map((hour) => hour.timeSlot),
                    datasets: [
                        {
                            label: 'Total Orders',
                            data: hourlyBreakdown.map((hour) => hour.totalOrders),
                            backgroundColor: '#0ea5e9',
                            borderRadius: 6,
                            barPercentage: 0.6,
                            yAxisID: 'y'
                        },
                        {
                            label: 'Total Revenue (₹)',
                            data: hourlyBreakdown.map((hour) => hour.totalRevenue),
                            type: 'line',
                            fill: true,
                            backgroundColor: gradient,
                            borderColor: '#5555c2',
                            tension: 0.4,
                            pointBackgroundColor: '#ffffff',
                            pointBorderColor: '#5555c2',
                            pointBorderWidth: 2,
                            yAxisID: 'y1'
                        },
                    ],
                },
                options: {
                    responsive: true, maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: { legend: { position: 'top', labels: { usePointStyle: true, padding: 20 } } },
                    scales: {
                        x: { grid: { display: false } },
                        y: { type: 'linear', display: true, position: 'left', beginAtZero: true, grid: { color: '#f1f5f9' } },
                        y1: { type: 'linear', display: true, position: 'right', beginAtZero: true, grid: { display: false } },
                    }
                }
            });
        }

        // 2. Order Type (Donut)
        const orderTypeCtx = document.getElementById('orderTypeChart');
        if (orderTypeCtx) {
            chartRefs.current.orderType = new Chart(orderTypeCtx, {
                type: 'doughnut',
                data: {
                    labels: ['Dine In', 'Delivery', 'Takeaway'],
                    datasets: [{
                        data: [balanceData.dineIn.totalOrders, balanceData.onlineDelivery.totalOrders, balanceData.takeAway.totalOrders],
                        backgroundColor: ['#f97316', '#84cc16', '#06b6d4'],
                        borderWidth: 0,
                        hoverOffset: 4
                    }]
                },
                options: {
                    responsive: true, maintainAspectRatio: false,
                    cutout: '70%',
                    plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, padding: 15 } } }
                }
            });
        }

        // 3. Payment Mode (Donut)
        const paymentCtx = document.getElementById('paymentChart');
        if (paymentCtx) {
            chartRefs.current.payment = new Chart(paymentCtx, {
                type: 'doughnut',
                data: {
                    labels: ['Cash', 'Card', 'UPI'],
                    datasets: [{
                        data: [paymentData.cash.totalOrders, paymentData.card.totalOrders, paymentData.upi.totalOrders],
                        backgroundColor: ['#0ea5e9', '#eab308', '#22c55e'],
                        borderWidth: 0,
                        hoverOffset: 4
                    }]
                },
                options: {
                    responsive: true, maintainAspectRatio: false,
                    cutout: '70%',
                    plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, padding: 15 } } }
                }
            });
        }

        // 4. Top Selling Items (Horizontal Bar)
        const topItemsCtx = document.getElementById('topItemsChart');
        if (topItemsCtx) {
            chartRefs.current.topItems = new Chart(topItemsCtx, {
                type: 'bar',
                data: {
                    labels: topItemsData.map(item => item.name),
                    datasets: [{
                        label: 'Quantity Sold',
                        data: topItemsData.map(item => item.qty),
                        backgroundColor: '#8b5cf6',
                        borderRadius: 4,
                        barPercentage: 0.7
                    }]
                },
                options: {
                    indexAxis: 'y',
                    responsive: true, maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                        x: { beginAtZero: true, grid: { color: '#f1f5f9' } },
                        y: { grid: { display: false } }
                    }
                }
            });
        }

        // 5. Daily Sales Trend (Area Chart)
        const dailyCtx = document.getElementById('dailyChart');
        if (dailyCtx) {
            const gradientDaily = dailyCtx.getContext('2d').createLinearGradient(0, 0, 0, 400);
            gradientDaily.addColorStop(0, 'rgba(16, 185, 129, 0.4)');
            gradientDaily.addColorStop(1, 'rgba(16, 185, 129, 0.0)');

            chartRefs.current.daily = new Chart(dailyCtx, {
                type: 'line',
                data: {
                    labels: dailyBreakdown.map(d => d.date),
                    datasets: [{
                        label: 'Daily Revenue (₹)',
                        data: dailyBreakdown.map(d => d.totalRevenue),
                        fill: true,
                        backgroundColor: gradientDaily,
                        borderColor: '#10b981',
                        tension: 0.4,
                        pointBackgroundColor: '#ffffff',
                        pointBorderColor: '#10b981',
                    }]
                },
                options: {
                    responsive: true, maintainAspectRatio: false,
                    plugins: { legend: { position: 'top', labels: { usePointStyle: true } } },
                    scales: {
                        x: { grid: { display: false } },
                        y: { beginAtZero: true, grid: { color: '#f1f5f9' } }
                    }
                }
            });
        }
    };

    const handleBackToFirstTab = () => {
        navigate('/home');
    };

    const handleResetFilters = () => {
        setFromDate(null);
        setToDate(null);
        setFilterOrderType('');
        setFilterPaymentMode('');
    };

    const toggleList = () => {
        setShowList(!showList);
    };

    if (loading || configLoading) {
        return (
            <div className="pos-balance-container loading-state">
                <div className="spinner-border text-light" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div className="mt-2 text-light">Loading Analytics Data...</div>
            </div>
        );
    }

    if (!canRead) {
        return (
            <div className="pos-balance-container container text-center mt-5">
                <div className="alert alert-danger shadow-lg">
                    <h3>Access Denied</h3>
                    <p>You do not have permission to view POS Balance & Analytics.</p>
                </div>
                <button className="btn" style={{ backgroundColor: '#5555c2', color: 'white' }} onClick={() => navigate("/home")}>
                    Back to Home
                </button>
            </div>
        );
    }

    if (error) {
        return (
            <div className="pos-balance-container error-state">
                <div className="alert alert-danger shadow-lg" role="alert">
                    <i className="fa-solid fa-triangle-exclamation me-2"></i> {error}
                </div>
            </div>
        );
    }

    return (
        <div className="pos-balance-container" style={{ minHeight: '100vh', paddingBottom: '30px', position: 'relative', paddingTop: '20px' }}>
            {/* Back Button */}
            <button
                onClick={() => navigate('/home')}
                className="back-btn"
                style={{ position: 'absolute', top: '20px', left: '20px', width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'white', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10 }}
                aria-label="Back to Home"
            >
                <i className="fa-solid fa-arrow-left" style={{ fontSize: '18px' }}></i>
            </button>

            <div style={{ position: 'absolute', top: '20px', right: '20px', display: 'flex', gap: '12px', zIndex: 10 }}>
                <button onClick={() => navigate('/salespage')} style={{ background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', padding: '10px 20px', borderRadius: '12px', fontWeight: '700', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fa-solid fa-chart-bar"></i> Sales Page
                </button>
                <button onClick={() => navigate('/sales-reports')} style={{ background: '#604BE8', color: '#ffffff', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: '700', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(96, 75, 232, 0.2)' }}>
                    <i className="fa-solid fa-chart-line"></i> Sales Report
                </button>
            </div>

            <div className="header-row text-center mb-4">
                <h1 className="page-title" style={{ fontWeight: '800', color: '#1e293b', margin: '0' }}>POS Balance & Analytics</h1>
                <p className="page-subtitle" style={{ color: '#475569', fontSize: '15px', marginTop: '5px' }}>Track your sales performance in real-time</p>
            </div>

            <div className="content-wrapper container-fluid" style={{ maxWidth: '100%', margin: '0 auto', padding: '0 40px' }}>

                {/* Top Filter Row */}
                <div className="glass-card filter-card mb-4 shadow-sm" style={{ backgroundColor: 'white', borderRadius: '12px' }}>
                    <div className="card-header-custom" style={{ backgroundColor: '#5555c2', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', padding: '12px 20px' }}>
                        <h5 className="card-title-custom text-white m-0" style={{ fontSize: '16px', fontWeight: '600' }}>
                            <i className="fa-solid fa-filter me-2"></i> Filters
                        </h5>
                    </div>
                    <div className="card-body-custom p-4">
                        <div className="row g-3 align-items-end">
                            <div className="col-lg-2 col-md-4">
                                <label className="form-label" style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '6px' }}>From Date</label>
                                <DatePicker
                                    selected={fromDate}
                                    onChange={(date) => setFromDate(date)}
                                    dateFormat="yyyy-MM-dd"
                                    className="form-control"
                                    placeholderText="Start Date"
                                />
                            </div>
                            <div className="col-lg-2 col-md-4">
                                <label className="form-label" style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '6px' }}>To Date</label>
                                <DatePicker
                                    selected={toDate}
                                    onChange={(date) => setToDate(date)}
                                    dateFormat="yyyy-MM-dd"
                                    className="form-control"
                                    placeholderText="End Date"
                                    minDate={fromDate}
                                />
                            </div>
                            <div className="col-lg-3 col-md-4">
                                <label className="form-label" style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '6px' }}>Order Type</label>
                                <select
                                    value={filterOrderType}
                                    onChange={(e) => setFilterOrderType(e.target.value)}
                                    className="form-select"
                                >
                                    <option value="">All Types</option>
                                    <option value="Dine In">Dine In</option>
                                    <option value="Takeaway">Takeaway</option>
                                    <option value="Online Delivery">Online Delivery</option>
                                </select>
                            </div>
                            <div className="col-lg-3 col-md-6">
                                <label className="form-label" style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '6px' }}>Payment Mode</label>
                                <select
                                    value={filterPaymentMode}
                                    onChange={(e) => setFilterPaymentMode(e.target.value)}
                                    className="form-select"
                                >
                                    <option value="">All Modes</option>
                                    <option value="Cash">Cash</option>
                                    <option value="Card">Card</option>
                                    <option value="UPI">UPI</option>
                                    <option value="ZOMATO">ZOMATO</option>
                                    <option value="SWIGGY">SWIGGY</option>
                                    <option value="NOON">NOON</option>
                                </select>
                            </div>
                            <div className="col-lg-2 col-md-6">
                                <button className="btn btn-reset w-100 py-2" style={{ backgroundColor: '#f8fafc', color: '#5555c2', border: '1px solid #cbd5e1', fontWeight: '600', borderRadius: '8px', transition: 'all 0.2s' }} onClick={handleResetFilters}>
                                    Reset Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row g-4">
                    <div className="col-12">
                        {/* Summary Header */}
                        <div className="glass-card summary-card mb-4 d-flex justify-content-between align-items-center p-4 shadow-sm" style={{ backgroundColor: 'white', borderRadius: '12px' }}>
                            <div className="revenue-display">
                                <span className="label text-muted" style={{ fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Revenue</span>
                                <div className="value mt-1" style={{ fontSize: '32px', fontWeight: '800', color: '#5555c2' }}>₹{totalRevenue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                            </div>
                            <button className="btn" style={{ backgroundColor: '#5555c2', color: 'white', border: 'none', padding: '10px 24px', borderRadius: '8px', fontWeight: '600', display: 'flex', alignItems: 'center' }} onClick={toggleList}>
                                {showList ? <><i className="fa-solid fa-eye-slash me-2"></i> Hide List</> : <><i className="fa-solid fa-eye me-2"></i> Show List</>}
                            </button>
                        </div>

                        {/* Breakdown List (Conditional) */}
                        {showList && (
                            <div className="glass-card table-section p-4 shadow-sm mb-4" style={{ backgroundColor: 'white', borderRadius: '12px' }}>
                                <h3 className="section-title mb-3" style={{ fontSize: '18px', fontWeight: '700', color: '#334155' }}>Hourly Breakdown</h3>
                                <div className="table-responsive">
                                    <table className="table custom-table table-hover align-middle">
                                        <thead style={{ backgroundColor: '#f8fafc' }}>
                                            <tr>
                                                <th style={{ color: '#64748b', fontWeight: '600', borderBottom: '1px solid #e2e8f0', padding: '12px 16px' }}>Time Slot</th>
                                                <th style={{ color: '#64748b', fontWeight: '600', borderBottom: '1px solid #e2e8f0', padding: '12px 16px' }}>Orders</th>
                                                <th style={{ color: '#64748b', fontWeight: '600', borderBottom: '1px solid #e2e8f0', padding: '12px 16px' }}>Revenue</th>
                                                <th style={{ color: '#64748b', fontWeight: '600', borderBottom: '1px solid #e2e8f0', padding: '12px 16px' }}>Mode</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {hourlyBreakdown.map((hour) => (
                                                <tr key={hour.timeSlot}>
                                                    <td style={{ padding: '12px 16px', color: '#334155', fontWeight: '500' }}>{hour.timeSlot}</td>
                                                    <td style={{ padding: '12px 16px', color: '#334155' }}>{hour.totalOrders}</td>
                                                    <td style={{ padding: '12px 16px', color: '#0f172a', fontWeight: '600' }}>₹{hour.totalRevenue.toFixed(2)}</td>
                                                    <td style={{ padding: '12px 16px' }}>
                                                        <span style={{ backgroundColor: '#e2e8f0', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', color: '#475569', fontWeight: '500' }}>
                                                            {hour.paymentMode}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Chart Area 1: Hourly Sales (Mixed Bar + Line) */}
                        <div className="glass-card chart-section p-4 mb-4 shadow-sm" style={{ backgroundColor: 'white', borderRadius: '12px' }}>
                            <h3 className="section-title mb-4" style={{ fontSize: '18px', fontWeight: '700', color: '#334155' }}>Hourly Orders & Revenue</h3>
                            <div className="chart-container-wrapper" style={{ height: '350px' }}>
                                <canvas id="hourlyChart"></canvas>
                            </div>
                        </div>

                        {/* Chart Area 2: Donuts & Horizontal Bar */}
                        <div className="row g-4 mb-4">
                            {/* Order Type Donut */}
                            <div className="col-lg-4 col-md-12">
                                <div className="glass-card chart-section p-4 shadow-sm h-100" style={{ backgroundColor: 'white', borderRadius: '12px' }}>
                                    <h3 className="section-title mb-4" style={{ fontSize: '16px', fontWeight: '700', color: '#334155', textAlign: 'center' }}>Orders by Type</h3>
                                    <div className="chart-container-wrapper" style={{ height: '250px' }}>
                                        <canvas id="orderTypeChart"></canvas>
                                    </div>
                                </div>
                            </div>

                            {/* Payment Mode Donut */}
                            <div className="col-lg-4 col-md-12">
                                <div className="glass-card chart-section p-4 shadow-sm h-100" style={{ backgroundColor: 'white', borderRadius: '12px' }}>
                                    <h3 className="section-title mb-4" style={{ fontSize: '16px', fontWeight: '700', color: '#334155', textAlign: 'center' }}>Payment Methods</h3>
                                    <div className="chart-container-wrapper" style={{ height: '250px' }}>
                                        <canvas id="paymentChart"></canvas>
                                    </div>
                                </div>
                            </div>

                            {/* Top Items Horizontal Bar */}
                            <div className="col-lg-4 col-md-12">
                                <div className="glass-card chart-section p-4 shadow-sm h-100" style={{ backgroundColor: 'white', borderRadius: '12px' }}>
                                    <h3 className="section-title mb-4" style={{ fontSize: '16px', fontWeight: '700', color: '#334155', textAlign: 'center' }}>Top Selling Items</h3>
                                    <div className="chart-container-wrapper" style={{ height: '250px' }}>
                                        <canvas id="topItemsChart"></canvas>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Chart Area 3: Daily Sales (Area Chart) */}
                        <div className="glass-card chart-section p-4 mb-4 shadow-sm" style={{ backgroundColor: 'white', borderRadius: '12px' }}>
                            <h3 className="section-title mb-4" style={{ fontSize: '18px', fontWeight: '700', color: '#334155' }}>Daily Revenue Trend</h3>
                            <div className="chart-container-wrapper" style={{ height: '300px' }}>
                                <canvas id="dailyChart"></canvas>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}

export default PosBalance;
